import React, { useCallback, useContext, useEffect, useState } from "react";

import SecondaryNav from "../../shared/components/secondary-nav/secondary-nav";
import ShIcon from "../../shared/components/sh-icon/sh-icon";
import ShLink from "../../shared/components/sh-link/sh-link";
import { Media } from "../../shared/contexts/media/MediaContext";
import { UiStateContext } from "../../shared/contexts/ui-state/UiStateContext";
import useColumnEnd from "../../shared/hooks/column-end/useColumnEnd";
import { GlobalNavContent, IconPack, SecondaryNavContent } from "../../shared/models/app-data-model";
import AuxNav from "../aux-nav/aux-nav";
import "../shared/navigation-shared.scss";
import "./global-nav-desktop.scss";

export type GlobalNavDesktopProps = {
  content: GlobalNavContent;
  iconPack: IconPack[];
  lightFontMode?: boolean;
  subsiteNavPresent?: boolean;
};

const GlobalNavDesktop: React.FC<GlobalNavDesktopProps> = ({
  content,
  iconPack,
  lightFontMode,
  subsiteNavPresent
}: GlobalNavDesktopProps) => {
  const [selectedSecondaryNavItem, setSelectedSecondaryNavItem] = useState([] as SecondaryNavContent[]);
  const [columns, setContent] = useColumnEnd(selectedSecondaryNavItem as SecondaryNavContent[]);
  const [overflow, setOverflow] = useState(false);

  // This hook is responsible for updating our column-end hook whenever our content changes
  useEffect(() => {
    setContent(selectedSecondaryNavItem as SecondaryNavContent[]);
  }, [selectedSecondaryNavItem, setContent]);

  const uiStateContext = useContext(UiStateContext);

  const toggleMegaMenu = useCallback(
    (index: number, isItemOpen: boolean, NavItemData: SecondaryNavContent[]): void => {
      uiStateContext.setAuxNavOpen?.(false);
      uiStateContext.setSubsiteMenuOpen?.(false);
      uiStateContext.setMenuOpen?.(!isItemOpen);
      uiStateContext.setMenuOpenIndex?.(index);
      setSelectedSecondaryNavItem(NavItemData);
    },
    [uiStateContext.setAuxNavOpen, uiStateContext.setSubsiteMenuOpen, uiStateContext.setMenuOpen, uiStateContext.setMenuOpenIndex]
  );

  const checkOverflow = useCallback((): void => {
    const elCheck = typeof document !== "undefined" && document.querySelector(".global-nav");
    // measure if nav elements are overflowing container
    if (elCheck && elCheck?.clientHeight < elCheck?.scrollHeight) {
      // items are overflowed need to add fix class.
      setOverflow(true);
    }
  }, [setOverflow]);

  const handleResize = useCallback((): void => {
    setOverflow(false); // clear out overflow check on resize
    checkOverflow();
  }, [setOverflow, checkOverflow]);

  useEffect(() => {
    // check initial load if class needs to be added. Delayed to ensure nav loaded.
    const delayInitialOverflowCheck = setTimeout(() => {
      checkOverflow();
    }, 500);

    return () => clearTimeout(delayInitialOverflowCheck);
  }, [checkOverflow]);

  useEffect(() => {
    window?.addEventListener("resize", handleResize);
    return () => window?.removeEventListener("resize", handleResize);
  }, [handleResize]);

  const textColorClass = lightFontMode && !subsiteNavPresent ? "white" : "";

  return (
    <Media greaterThanOrEqual="desktop">
      <nav
        id="global-nav-desktop"
        className={`global-nav ${overflow ? "global-nav-condensed" : ""}`}
        data-testid={overflow ? "globalNavCondensed" : "globalNav"}
      >
        {content?.primary_nav?.map((item, index) => {
          const navItemWithChildren = item.primary_nav_item?.reference.find((el) => el.primary_nav);
          const navItemWithLink = item.primary_nav_item?.reference.find((el) => el.url || el.link);
          const navItemOpenClass = uiStateContext.menuOpen && uiStateContext.menuOpenIndex === index ? "open" : "";
          const isItemOpen = uiStateContext.menuOpen && uiStateContext.menuOpenIndex === index;

          if (navItemWithChildren) {
            return (
              <button
                className={`primary-nav-item unstyled-btn ${navItemOpenClass} ${textColorClass}`}
                id={`primary-nav-item-${index}`}
                data-testid={`primary-nav-item-test-id-${index}`}
                key={index}
                onClick={() => {
                  toggleMegaMenu(index, isItemOpen, navItemWithChildren.primary_nav as SecondaryNavContent[]);
                }}
              >
                {item.primary_nav_item.title}
                <ShIcon
                  iconPack={iconPack}
                  iconName="caret_down"
                  className={`icon ${lightFontMode ? "filter-white" : ""}`}
                  elemId={`primary-nav-item-${index}-icon`}
                />
              </button>
            );
          } else if (navItemWithLink) {
            return (
              <ShLink
                key={index}
                title={item.primary_nav_item.title}
                className={`primary-nav-item ${textColorClass}`}
                reference={item.primary_nav_item.reference}
              />
            );
          }
        })}
      </nav>
      {uiStateContext.menuOpen && columns.length > 0 && (
        <div className="secondary-nav-wrapper" data-testid="test-secondary-nav-render">
          {selectedSecondaryNavItem && <SecondaryNav columns={columns} isOutsideClickHandlerEnabled={true} />}
        </div>
      )}
      {uiStateContext.auxNavOpen && columns.length === 0 && (
        <div className="aux-nav-wrapper">{content && content.auxiliary_nav && <AuxNav iconPack={iconPack} content={content} />}</div>
      )}
    </Media>
  );
};

export default GlobalNavDesktop;
